/**
 *
 * shareUrl
 * Share a url through the Web Share API. Resolves true when the share sheet
 * handled it (including when the user cancelled), false when sharing is
 * unavailable or did not happen, so the caller can fall back to copying.
 *
 */

import { reportError } from 'utils/Sentry';

// Rejections that describe the browser or the user, not a bug of ours: the
// user dismissed the sheet, the document has no user activation (or web-share
// is blocked by permissions policy), or a share is already open.
const EXPECTED_ERRORS = ['AbortError', 'NotAllowedError', 'InvalidStateError'];

let sharing = false;

function canShare(data) {
  if (!navigator.share) {
    return false;
  }
  return !navigator.canShare || navigator.canShare(data);
}

async function shareUrl(url) {
  const data = { url };
  // A share that is still open would reject with InvalidStateError, and a
  // promise that never settles must not disable the button, so leave the
  // second attempt to the copy fallback.
  if (sharing || !canShare(data)) {
    return false;
  }
  sharing = true;
  try {
    await navigator.share(data);
    return true;
  } catch (err) {
    if (!EXPECTED_ERRORS.includes(err?.name)) {
      reportError(err, { url });
    }
    // Cancelling is a deliberate choice, not a reason to copy instead
    return err?.name === 'AbortError';
  } finally {
    sharing = false;
  }
}

export default shareUrl;
